import { LOADING_STATES } from '../types/redux';
import { RatingColor, Locator } from '../types/ui';
// États de chargement
export { LOADING_STATES };

// URLs de l'API
export const API_ENDPOINTS = Object.freeze({
    NOTES: '/api/notes',
    THESAURUS: '/api/thesaurus',
    PERIMETRES: '/api/perimetres',
    ENTREES: '/api/entrees',
} as const);

// Configuration des modales
export const MODAL_MODES = Object.freeze({
    CREATE: 'create',
    EDIT: 'edit',
    VIEW: 'view',
} as const);

// Configuration des thèmes
export const THEMES = Object.freeze({
    LIGHT: 'light',
    DARK: 'dark',
} as const);

// Configuration des langues
export const LANGUAGES = Object.freeze({
    FR: 'fr',
    EN: 'en',
} as const);

// Configuration du tri
export const SORT_DIRECTIONS = Object.freeze({
    ASC: 'asc',
    DESC: 'desc',
    NONE: null,
} as const);

// Types de colonnes pour les tables
export const COLUMN_TYPES = Object.freeze({
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
    BOOLEAN: 'boolean',
} as const);

// Configuration des breakpoints Material-UI
export const BREAKPOINTS = Object.freeze({
    XS: 'xs',
    SM: 'sm',
    MD: 'md',
    LG: 'lg',
    XL: 'xl',
} as const);

/** Nom conventionnel du périmètre « fourre-tout » — miroir de api/src/models/Perimetre.ts */
export const GLOBAL_PERIMETRE_NAME = 'GLOBAL';

// Configuration des couleurs par défaut pour les périmètres
export const DEFAULT_PERIMETRE_COLORS = Object.freeze([
    '#2E7D32', // Emerald
    '#388e3c', // Green
    '#f57c00', // Orange
    '#7b1fa2', // Purple
    '#c2185b', // Pink
    '#00796b', // Teal
    '#1B5E20', // Emerald Dark
    '#689f38', // Light Green
] as const);

// Couleurs pour distinguer les éléments partagés vs propriétaires
export const SHARED_COLORS = Object.freeze({
    // Thésaurus/Entrées propriétaires (emerald)
    PROPRIETARY: {
        main: '#2E7D32',      // Emerald primary
        light: '#4CAF50',
        dark: '#1B5E20',
        bg: 'rgba(46, 125, 50, 0.08)',
        bgLight: 'rgba(46, 125, 50, 0.04)',
        border: '#2E7D32'
    },
    // Thésaurus partagé dont JE suis propriétaire (bleu-vert/teal)
    SHARED_OWNED: {
        main: '#00897b',      // Teal
        light: '#4db6ac',
        dark: '#00695c',
        bg: 'rgba(0, 137, 123, 0.08)',
        bgLight: 'rgba(0, 137, 123, 0.04)',
        border: '#00897b'
    },
    // Thésaurus/Entrées partagés avec moi (violet)
    SHARED_WITH_ME: {
        main: '#7b1fa2',      // Purple
        light: '#ab47bc',
        dark: '#6a1b9a',
        bg: 'rgba(123, 31, 162, 0.08)',
        bgLight: 'rgba(123, 31, 162, 0.04)',
        border: '#7b1fa2'
    },
    // Entrées partagées (dans le contexte d'un thésaurus partagé)
    SHARED_ENTRY: {
        main: '#7b1fa2',      // Purple (même que SHARED_WITH_ME pour cohérence)
        light: '#ab47bc',
        dark: '#6a1b9a',
        bg: 'rgba(123, 31, 162, 0.08)',
        bgLight: 'rgba(123, 31, 162, 0.04)',
        border: '#7b1fa2'
    }
} as const);

// Couleurs de rating (compatibilité avec testreact)
export const RATING_COLORS: readonly RatingColor[] = Object.freeze([
    { bgcolor: '#ffebee', color: '#000000' },
    { bgcolor: '#ef9a9a', color: '#000000' },
    { bgcolor: '#ef5350', color: '#ffffff' },
    { bgcolor: '#e53935', color: '#ffffff' },
    { bgcolor: '#b71c1c', color: '#ffffff' }
]);

// Configuration des localisateurs (compatibilité avec testreact)
export const LOCATORS: readonly Locator[] = Object.freeze([
    { name: "pages", displayName: "P" },
    // ⚠️ `chapters` (pluriel) : c'est la clé du LocatorSchema de l'API — `chapter`
    // était écarté en silence par Mongoose (schéma strict), la saisie se perdait.
    { name: "chapters", displayName: "Chp" },
    { name: "paragraphs", displayName: "§" },
    { name: "lines", displayName: "Ln" }
]);

// Configuration de l'application
export const APP_CONFIG = Object.freeze({
    MAX_ITEMS_DEFAULT: 10,
    AUTO_REFRESH: true,
    PAGINATION_DEFAULT: 25,
} as const);

// Messages d'erreur par défaut
export const ERROR_MESSAGES = Object.freeze({
    GENERIC: 'Une erreur est survenue',
    NETWORK: 'Erreur de connexion réseau',
    UNAUTHORIZED: 'Accès non autorisé',
    FORBIDDEN: 'Accès interdit',
    NOT_FOUND: 'Ressource non trouvée',
    VALIDATION: 'Données invalides',
} as const);

// Messages de succès
export const SUCCESS_MESSAGES = Object.freeze({
    CREATED: 'Élément créé avec succès',
    UPDATED: 'Élément mis à jour avec succès',
    DELETED: 'Élément supprimé avec succès',
    SAVED: 'Sauvegardé avec succès',
    NOTE_CREATED: 'Note créée avec succès',
    NOTE_UPDATED: 'Note mise à jour avec succès',
    NOTE_DELETED: 'Note supprimée avec succès',
    ENTREE_CREATED: 'Concept créé',
    ENTREE_UPDATED: 'Concept mis à jour',
    ENTREE_DELETED: 'Concept supprimé',
    PERIMETRE_CREATED: 'Dossier créé avec succès',
    PERIMETRE_UPDATED: 'Dossier mis à jour avec succès',
    PERIMETRE_DELETED: 'Dossier supprimé avec succès',
} as const);

// Niveaux de sévérité des notifications
export const NOTIFICATION_SEVERITY = Object.freeze({
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
} as const);

// Configuration de debounce
export const DEBOUNCE_DELAYS = Object.freeze({
    SEARCH: 300,
    AUTO_SAVE: 1000,
    RESIZE: 150,
} as const);

// Tailles de pagination
export const PAGINATION_SIZES = Object.freeze([10, 25, 50, 100] as const);

// Configuration du text editor
export const TEXT_EDITOR_CONFIG = Object.freeze({
    AUTO_FOCUS: true,
    SPELL_CHECK: true,
    MAX_LENGTH: 10000,
    PLACEHOLDER: 'Commencez à écrire...',
} as const);

// Styles UI constants
export const UI_STYLES = Object.freeze({
    LAYOUT: {
        SEARCH_CONTAINER: {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
            },
            marginRight: 1,
            marginLeft: 0,
            width: '100%',
            display: 'flex',
            borderRadius: 1,
        },
    },
    COMPONENTS: {
        SEARCH_BAR_WIDTH: (width: number | null) => ({
            width: width || 'auto',
            zIndex: 1500, // = Z_INDEX.PANEL_DROPDOWN
        }),
    },
    Z_INDEX: {
        // ── Base UI ──
        TAB_BAR: 1080,                      // Barre d'onglets du shell mobile (C11) : au-dessus du contenu, sous l'AppBar et toute modale
        APP_BAR: 1100,
        APP_BAR_CONTENT: 1150,              // Contenu dans l'AppBar (CBPerimetre)
        APP_BAR_DROPDOWN: 1160,             // Dropdown du CBPerimetre
        DRAWER: 1200,
        BACKDROP: 1200,
        // ── Modals standard ──
        MODAL: 1300,
        SHEET: 1300,                        // AgBottomSheet (C11) : la modale du mobile, même strate que MODAL — jamais deux sheets ouvertes
        MODAL_CONTENT: 1350,                // Contenu/overlays dans un modal
        MODAL_DROPDOWN: 1400,               // Dropdown dans un modal
        PANELS: 1400,                       // Panels (FilePanel, ChatPanel, EntreePanel, CommentPanel)
        MODAL_LOADING: 1450,
        MODAL_ACTIONS: 1480,                // Boutons d'action du modal (au-dessus de la toolbar éditeur)
        // ── TextEditor plugins & sous-éléments ──
        STICKY_NOTE: 1500,                  // Plugin StickyNote (au-dessus de ModalNote)
        PANEL_DROPDOWN: 1500,               // Dropdowns portés dans les panels (Select, Autocomplete)
        PANEL_AGRANDI: 1520,                // Fenêtre agrandie ouverte depuis un panneau (graphe des Méta Notes) : au-dessus des panneaux (1400) et de leurs listes (1500)
        HISTORY_PANEL: 1550,                // Panel d'historique des notes
        HISTORY_PANEL_MODAL: 1560,          // Modales ouvertes depuis le panel d'historique (aperçu, comparaison)
        EXCALIDRAW_MODAL: 1600,             // Plugin ExcalidrawNode (au-dessus de StickyNote)
        EDITOR_DIALOG: 1610,                // Dialogues de l'éditeur portalisés dans body (toolbar « Insert » : image, tableau, équation, embed ; confirmation Excalidraw) : au-dessus de ModalNote et d'Excalidraw (était 100, passait dessous)
        // ── Overlays globaux ──
        TOOLTIP: 1700,
        POPOVER: 1800,
        // ── Lecteur de documents ──
        DOC_READER: 1900,                   // DocReader modal/fullpage
        DOC_READER_DROPDOWN: 1940,          // Listes portalisées ouvertes depuis le lecteur (mot-clé de la barre de sélection)
        DOC_READER_TOOLTIP: 1950,           // Tooltips dans le DocReader
        ENTREE_MODAL: 1955,                 // Fenêtre d'un concept (ModalEntree), ouverte aussi depuis le lecteur : au-dessus du DocReader et de ses listes, sous « Partager la note » (était 1950 en dur)
        ENTREE_CONFIRM: 1957,               // Confirmation ouverte depuis la fenêtre d'un concept (EntreeDeleteConfirm) : au-dessus de la fenêtre (1955), sous « Partager la note »
        NOTE_SHARE_MODAL: 1960,             // Dialogue « Partager la note » (C8) : ouvert depuis l'éditeur OU depuis la barre d'état après une citation, lecteur encore ouvert — au-dessus du DocReader (était 1580, passait dessous)
        // ── Modals spéciaux ──
        ENTREES_MINER_MODAL: 2000,          // Modal EntreesMiner
        EDITOR_FLOATING_TRIGGER: 2010,      // Boutons posés sur le texte de l'éditeur (« Ajouter un commentaire », boutons des dialogues) : sous ses menus flottants
        EDITOR_FLOATING: 2030,              // Menus flottants de l'éditeur (typeahead, listes de la barre d'outils, nuancier, saisie d'un commentaire, popover des entrées, liste de SearchBarEntries) : au-dessus d'EntreesMiner, qui monte un éditeur (étaient 2030 / 2001 en dur)
        BULK_REVIEW_MODAL: 2050,            // Revue d'un lot d'opérations en masse (ouverte depuis un panneau ou une modale)
        // ── Overlays bloquants ──
        OVERLAY_BLOCKING: 2100,             // Overlays bloquants plein écran (suppression, traitement)
        QUICK_CAPTURE_MODAL: 2150,          // Capture rapide (C4) : au-dessus de tout contexte d'ouverture (DocReader, ModalNote, revue de lot), sous les overlays d'aide
        // ── Help & notifications ──
        HELP_MODAL: 2200,                   // Modal d'aide (au-dessus de tout sauf snackbar)
        SNACKBAR: 2500,                     // Notifications (toujours visible)
    },
} as const); export type ApiEndpoint = typeof API_ENDPOINTS[keyof typeof API_ENDPOINTS];
export type ModalMode = typeof MODAL_MODES[keyof typeof MODAL_MODES];
export type Theme = typeof THEMES[keyof typeof THEMES];
export type Language = typeof LANGUAGES[keyof typeof LANGUAGES];
export type SortDirection = typeof SORT_DIRECTIONS[keyof typeof SORT_DIRECTIONS];
export type ColumnType = typeof COLUMN_TYPES[keyof typeof COLUMN_TYPES];
export type Breakpoint = typeof BREAKPOINTS[keyof typeof BREAKPOINTS];
